export const COMPOSER_ERP_REFERENCE_SCHEME = "aveda-erp:";

export const COMPOSER_ERP_REFERENCE_ENTITY_TYPES = [
  "customer",
  "project",
  "lead",
  "billing",
  "inventory",
  "planning",
  "communication",
  "dashboard",
  "settings",
] as const;

export type ComposerErpReferenceEntityType = (typeof COMPOSER_ERP_REFERENCE_ENTITY_TYPES)[number];

export interface ComposerErpReference {
  readonly entityType: ComposerErpReferenceEntityType;
  readonly id: string;
  readonly label: string;
  readonly toolId: string;
}

const entityTypeSet = new Set<string>(COMPOSER_ERP_REFERENCE_ENTITY_TYPES);

function escapeMarkdownLinkLabel(label: string): string {
  return label.replaceAll("\\", "\\\\").replaceAll("[", "\\[").replaceAll("]", "\\]");
}

function decodeMaybe(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function isComposerErpReferenceEntityType(
  value: string,
): value is ComposerErpReferenceEntityType {
  return entityTypeSet.has(value);
}

export function serializeComposerErpReferenceLink(reference: ComposerErpReference): string {
  const label = escapeMarkdownLinkLabel(`${reference.entityType}: ${reference.label}`);
  const params = new URLSearchParams({
    tool: reference.toolId,
    label: reference.label,
  });
  return `[${label}](${COMPOSER_ERP_REFERENCE_SCHEME}//${reference.entityType}/${encodeURIComponent(reference.id)}?${params.toString()})`;
}

export function parseComposerErpReferenceLinkDestination(
  destination: string,
  label: string,
): ComposerErpReference | null {
  if (!destination.startsWith(`${COMPOSER_ERP_REFERENCE_SCHEME}//`)) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(destination);
  } catch {
    return null;
  }

  const entityType = url.hostname;
  if (!isComposerErpReferenceEntityType(entityType)) {
    return null;
  }

  const id = decodeMaybe(url.pathname.replace(/^\/+/, ""));
  const toolId = url.searchParams.get("tool")?.trim() ?? "";
  const queryLabel = url.searchParams.get("label")?.trim() ?? "";
  const normalizedLabel = label.replace(/^[^:]+:\s*/, "").trim();
  const referenceLabel = queryLabel || normalizedLabel;
  if (!id || !toolId || !referenceLabel) {
    return null;
  }

  return {
    entityType,
    id,
    label: referenceLabel,
    toolId,
  };
}
